'use client'

import { useState } from 'react'
import { Check, Gift } from 'lucide-react'
import BookingButton from '@/components/booking-button'
import { LeatherHeader, visualFor } from '@/components/rental-visuals'
import rentalDataEn from '@/content/rental-packs-2025-2026.json'
import rentalDataFr from '@/content/rental-packs-2025-2026.fr.json'
import rentalDataNl from '@/content/rental-packs-2025-2026.nl.json'
import rentalDataEs from '@/content/rental-packs-2025-2026.es.json'
import rentalDataIt from '@/content/rental-packs-2025-2026.it.json'

type RentalDuration = '4h' | '1day' | '2days' | '3days' | '4days' | '5days' | '6days' | '7days'

type RentalPack = {
 id: string
 name: string
 category: string
 description: string
 features: string[]
 pricing: Record<RentalDuration, number>
 ageRange?: string
 products?: Array<{
 name: string
 description: string
 photo: string
 }>
 separateItems?: {
 skisOnly?: Record<string, number>
 bootsOnly?: Record<string, number>
 skisOrSnowboardOnly?: Record<string, number>
 }
}

const getDurationLabels = (locale: string): Record<RentalDuration, string> => {
 if (locale === 'fr') {
 return {
 '4h': '4h',
 '1day': '1 Jour',
 '2days': '2 Jours',
 '3days': '3 Jours',
 '4days': '4 Jours',
 '5days': '5 Jours',
 '6days': '6 Jours',
 '7days': '7 Jours',
 }
 } else if (locale === 'es') {
 return {
 '4h': '4h',
 '1day': '1 Día',
 '2days': '2 Días',
 '3days': '3 Días',
 '4days': '4 Días',
 '5days': '5 Días',
 '6days': '6 Días',
 '7days': '7 Días',
 }
 } else if (locale === 'nl') {
 return {
 '4h': '4u',
 '1day': '1 Dag',
 '2days': '2 Dagen',
 '3days': '3 Dagen',
 '4days': '4 Dagen',
 '5days': '5 Dagen',
 '6days': '6 Dagen',
 '7days': '7 Dagen',
 }
 } else if (locale === 'it') {
 return {
 '4h': '4h',
 '1day': '1 Giorno',
 '2days': '2 Giorni',
 '3days': '3 Giorni',
 '4days': '4 Giorni',
 '5days': '5 Giorni',
 '6days': '6 Giorni',
 '7days': '7 Giorni',
 }
 } else {
 // English default
 return {
 '4h': '4h',
 '1day': '1 Day',
 '2days': '2 Days',
 '3days': '3 Days',
 '4days': '4 Days',
 '5days': '5 Days',
 '6days': '6 Days',
 '7days': '7 Days',
 }
 }
}

// Insurance pack definition
const insurancePackFallback: RentalPack = {
 id: 'insurance',
 name: 'INSURANCE',
 category: 'protection',
 description: 'Optional damage and theft insurance provides complete peace of mind during your rental. Protect yourself against accidental damage, loss, or theft of your equipment. Highly recommended for all skill levels and especially valuable for beginners and families.',
 features: [
 'Complete protection against equipment damage during use',
 'Coverage in case of theft or loss of rented equipment',
 'No additional charges for covered incidents',
 'Quick claim process with minimal paperwork',
 'Valid for the entire rental duration',
 'Covers accidental damage, theft protection, and loss coverage'
 ],
 pricing: {
 '4h': 2,
 '1day': 2,
 '2days': 4,
 '3days': 6,
 '4days': 8,
 '5days': 10,
 '6days': 12,
 '7days': 14,
 }
}

type WinterRentalPricingProps = {
 locale: string
 additionalEquipmentTitle?: string
 pricesTitle?: string
 adultPacksLabel?: string
 juniorPacksLabel?: string
 couponBanner?: string
 freeLabel?: string
 seventhDayFreeLabel?: string
 featuresLabel?: string
 benefitsLabel?: string
}

export default function WinterRentalPricing({ locale, additionalEquipmentTitle, pricesTitle, adultPacksLabel, juniorPacksLabel, couponBanner, freeLabel, seventhDayFreeLabel, featuresLabel, benefitsLabel }: WinterRentalPricingProps) {
 const [selectedDuration, setSelectedDuration] = useState<RentalDuration>('1day')
 const [selectedCategory, setSelectedCategory] = useState<'adult' | 'junior'>('adult')

 // Select the correct translation based on locale
 const getTranslatedData = () => {
 switch (locale) {
 case 'fr':
 return rentalDataFr
 case 'nl':
 return rentalDataNl
 case 'es':
 return rentalDataEs
 case 'it':
 return rentalDataIt
 default:
 return rentalDataEn
 }
 }

 const translatedData = getTranslatedData()
 const durationLabels = getDurationLabels(locale)
 const currentPacks = selectedCategory === 'adult' ? translatedData.adultPacks : translatedData.juniorPacks
 // Add insurance pack at the end
 const insurancePack = ((translatedData as unknown as { insurancePack?: RentalPack }).insurancePack) ?? insurancePackFallback
 const allPacks: RentalPack[] = [...currentPacks, insurancePack]

 // Helper to get the display text for price denominator
 const getPriceDenominator = (duration: RentalDuration) => {
 if (duration === '7days') {
 if (locale === 'fr') return '6 jours'
 if (locale === 'es') return '6 días'
 if (locale === 'nl') return '6 dagen'
 if (locale === 'it') return '6 giorni'
 return '6 days'
 }
 return durationLabels[duration].toLowerCase()
 }

 return (
 <div id="rental-prices" className="py-16 px-4 scroll-mt-20">
 <div className="max-w-7xl mx-auto">
 {/* Header */}
 <div className="text-center mb-12">
 <h2 className="text-4xl md:text-6xl font-bold font-[family-name:var(--font-bebas-neue)] mb-4">
 {pricesTitle || 'Rental Prices 2025/2026'}
 </h2>

 {/* Promotional Discount */}
 <div className="flex justify-center mt-8 mb-4">
 <div className="inline-flex items-center gap-2 bg-white text-neutral-900 px-6 py-3 rounded-full font-bold text-lg shadow-md border border-foreground/25">
 <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
 </svg>
 {couponBanner || 'Les Granges Residents: -20% with your coupon code!'}
 </div>
 </div>
 </div>

 {/* Category Selector */}
 <div className="flex justify-center mb-8">
 <div className="inline-flex items-center gap-1 rounded-full border border-foreground/25 bg-muted/60 p-1.5 shadow-sm">
 <button
 onClick={() => setSelectedCategory('adult')}
 className={`px-7 py-2.5 rounded-full text-base font-bold transition-all duration-200 ${
 selectedCategory === 'adult'
 ? 'bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white shadow-md'
 : 'text-foreground/70 hover:text-foreground hover:bg-background'
 }`}
 >
 {adultPacksLabel || 'Adult Packs'}
 </button>
 <button
 onClick={() => setSelectedCategory('junior')}
 className={`px-7 py-2.5 rounded-full text-base font-bold transition-all duration-200 ${
 selectedCategory === 'junior'
 ? 'bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white shadow-md'
 : 'text-foreground/70 hover:text-foreground hover:bg-background'
 }`}
 >
 {juniorPacksLabel || 'Junior Packs'}
 </button>
 </div>
 </div>

 {/* Duration Selector */}
 <div className="mb-12">
 <div className="flex flex-wrap justify-center gap-2 md:gap-3">
 {(Object.keys(durationLabels) as RentalDuration[]).map((duration) => (
 <button
 key={duration}
 onClick={() => setSelectedDuration(duration)}
 className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full font-bold transition-all duration-200 ${
 selectedDuration === duration
 ? 'bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white shadow-md scale-105'
 : 'bg-card text-foreground/80 border border-foreground/25 hover:border-primary/40 hover:text-foreground hover:shadow-sm'
 }`}
 >
 {durationLabels[duration]}
 {duration === '7days' && (
 <span className="inline-flex items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-emerald-600 shadow-sm ring-1 ring-emerald-500/20">
 <Gift className="w-3 h-3" />
 {freeLabel || 'FREE'}
 </span>
 )}
 </button>
 ))}
 </div>
 </div>

 {/* Pricing Cards */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
 {allPacks.map((pack) => {
 const basePrice = pack.pricing[selectedDuration]
 // For 7 days, show 6-day price (7th day free)
 const displayPrice = selectedDuration === '7days' ? pack.pricing['6days'] : basePrice
 const isInsurance = pack.id === 'insurance'
 const { color, Icon } = visualFor(pack.id)

 return (
 <div
 key={pack.id}
 className={`group relative bg-card border border-border rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden ${
 isInsurance ? 'md:col-span-2 lg:col-span-1' : ''
 }`}
 >
 {/* Colour-coded pack header — neutral with a colour accent */}
 <LeatherHeader name={pack.name} color={color} Icon={Icon} variant="leather" />

 <div className="p-6">
 {/* Price */}
 <div className="flex flex-col items-center gap-2.5 mb-6">
 {/* Main Pack Price */}
 <div className="flex items-baseline justify-center gap-x-2">
 <span className="text-5xl font-bold">{displayPrice}€</span>
 <span className="text-muted-foreground">
 / {getPriceDenominator(selectedDuration)}
 </span>
 </div>

 {/* Separate Item Pricing */}
 {!isInsurance && pack.separateItems && (
 <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 w-full text-sm">
 {pack.separateItems.skisOnly && (
 <span className="text-muted-foreground">
 {locale === 'fr' ? 'Skis seuls' : 'Skis only'}{' '}
 <span className="font-semibold text-foreground">
 {selectedDuration === '7days' && pack.separateItems.skisOnly['6days']
 ? pack.separateItems.skisOnly['6days']
 : pack.separateItems.skisOnly[selectedDuration]}€
 </span>
 </span>
 )}
 {pack.separateItems.skisOrSnowboardOnly && (
 <span className="text-muted-foreground">
 {locale === 'fr' ? 'Skis / snowboard' : 'Skis / snowboard'}{' '}
 <span className="font-semibold text-foreground">
 {selectedDuration === '7days' && pack.separateItems.skisOrSnowboardOnly['6days']
 ? pack.separateItems.skisOrSnowboardOnly['6days']
 : pack.separateItems.skisOrSnowboardOnly[selectedDuration]}€
 </span>
 </span>
 )}
 {pack.separateItems.bootsOnly && (
 <span className="text-muted-foreground">
 {locale === 'fr' ? 'Chaussures seules' : 'Boots only'}{' '}
 <span className="font-semibold text-foreground">
 {selectedDuration === '7days' && pack.separateItems.bootsOnly['6days']
 ? pack.separateItems.bootsOnly['6days']
 : pack.separateItems.bootsOnly[selectedDuration]}€
 </span>
 </span>
 )}
 </div>
 )}

 {/* 7th-day-free flag — after the prices */}
 {selectedDuration === '7days' && (
 <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-600 ring-1 ring-emerald-500/25">
 <Gift className="w-3.5 h-3.5" />
 {seventhDayFreeLabel || '7th Day FREE!'}
 </span>
 )}
 </div>

 {/* Booking Button - under main price */}
 <div className="mb-6">
 <BookingButton
 season="winter"
 currentLocale={locale}
 variant="gradient"
 gradientClass="from-nav-gradient-from to-nav-gradient-to"
 fullWidth={true}
 size="md"
 />
 </div>

 {/* Description - Large Space */}
 <div className="mb-4 min-h-[96px]">
 <p className="text-base leading-relaxed text-muted-foreground">
 {pack.description}
 </p>
 </div>

 {/* Features List - Display ALL features */}
 <div className="space-y-3">
 <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider">
 {isInsurance ? (benefitsLabel || 'Benefits') : (featuresLabel || 'Features')}
 </h4>
 {pack.features.map((feature, idx) => (
 <div key={idx} className="flex items-start gap-3">
 <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
 isInsurance ? 'text-emerald-600 ' : 'text-primary'
 }`} />
 <span className="text-sm text-muted-foreground leading-relaxed">{feature}</span>
 </div>
 ))}
 </div>
 </div>
 </div>
 )
 })}
 </div>

 {/* Other Rentals */}
 <div className="mt-16">
 {/* Section Title */}
 <div className="text-center mb-10">
 <h3 className="text-3xl md:text-4xl font-bold font-[family-name:var(--font-bebas-neue)]">
 {additionalEquipmentTitle || 'Additional Equipment'}
 </h3>
 </div>
 <div className="flex flex-wrap justify-center gap-6">
 {translatedData.otherRentals.map((item) => {
 const basePrice = item.pricing[selectedDuration]
 // For 7 days, show 6-day price (7th day free)
 const displayPrice = selectedDuration === '7days' ? item.pricing['6days'] : basePrice
 const { color, Icon } = visualFor(item.id)

 return (
 <div
 key={item.id}
 className="bg-card border border-border rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden w-full md:w-[calc(50%-12px)] lg:w-[calc(25%-18px)]"
 >
 {/* Colour-coded item header — neutral with a colour accent */}
 <LeatherHeader name={item.name} color={color} Icon={Icon} size="sm" variant="leather" />
 <div className="p-6">
 <div className="flex flex-col items-center gap-2 mb-5">
 <div className="flex items-baseline justify-center gap-x-2">
 <span className="text-3xl font-bold">{displayPrice}€</span>
 <span className="text-sm text-muted-foreground">
 / {getPriceDenominator(selectedDuration)}
 </span>
 </div>
 {selectedDuration === '7days' && (
 <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-600 ring-1 ring-emerald-500/25">
 <Gift className="w-3 h-3" />
 {seventhDayFreeLabel || '7th Day FREE!'}
 </span>
 )}
 </div>

 {/* Booking Button - under main price */}
 <div className="mb-4">
 <BookingButton
 season="winter"
 currentLocale={locale}
 variant="primary"
 fullWidth={true}
 size="sm"
 />
 </div>

 <p className="text-sm text-muted-foreground mb-4">
 {item.description}
 </p>
 <div className="space-y-2">
 {item.features.slice(0, 3).map((feature, idx) => (
 <div key={idx} className="flex items-start gap-2">
 <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
 <span className="text-xs text-muted-foreground leading-relaxed">{feature}</span>
 </div>
 ))}
 </div>
 </div>
 </div>
 )
 })}
 </div>
 </div>
 </div>
 </div>
 )
}
